import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { BaseWorkspaceSwitcher } from "./BaseWorkspaceSwitcher";
import { workspace as workspaceUtils } from "@rflocal/shared";
import { getTitleForPlan } from "@functions/plans/utilities";
import { twClassNames } from "../../helpers/twClassNames";

function getWorkspaceMembersCountText(members: Record<string, any> | undefined) {
    if (!members) return "0 Members";
    const membersCount =
        Object.keys(workspaceUtils.members.getMembersCountingTowardUsageLimitMap(members)).length ||
        1;
    return `${membersCount} Member${membersCount > 1 || membersCount === 0 ? "s" : ""}`;
}

export type BaseWorkspaceSwitcherButtonProps = {
    user: { name: string; email: string; profilePhoto?: string };
    currentWorkspace?: any;
    workspaces: { [id: string]: any };
    workspaceUsages?: { [id: string]: any };
    onWorkspaceSelect?: (workspaceId: string) => void;
    buttonClassName?: string;
    dropdownClassName?: string;
    dropdownPosition?: "left" | "right" | "center";
    searchThreshold?: number;
    theme?: "light" | "dark";
    hideChevron?: boolean;
    createNewWorkspace?: () => void;
    className?: string;
};

export function BaseWorkspaceSwitcherButton({
    user,
    currentWorkspace,
    workspaces,
    workspaceUsages,
    onWorkspaceSelect,
    buttonClassName = "",
    dropdownClassName = "",
    dropdownPosition = "left",
    searchThreshold = 5,
    theme = "dark",
    hideChevron = false,
    createNewWorkspace = null,
    className = ""
}: BaseWorkspaceSwitcherButtonProps) {
    // Don't show if no workspaces or only one workspace
    if (!workspaces) {
        return null;
    }

    // Calculate plan title if usage data is available
    const usage = workspaceUsages?.[currentWorkspace?.id];
    const isStarterTrial = usage?.plan === "starter" && usage?.trial?.isTrial;
    const planTitle = usage ? getTitleForPlan(usage.plan, isStarterTrial) : null;

    // Theme-based color classes
    const themeClasses = {
        light: {
            chevron: "text-gray-600 group-hover:text-gray-800",
            workspaceName: "text-gray-900",
            metadata: "text-gray-600",
            trailingChevron: "text-gray-600 group-hover:text-gray-800",
            workspaceSwitcherControl: "hover:bg-opacity-30 hover:bg-tint-active"
        },
        dark: {
            chevron: "text-gray-200 group-hover:text-white",
            workspaceName: "text-white",
            metadata: "text-gray-200",
            trailingChevron: "text-gray-200 group-hover:text-white",
            workspaceSwitcherControl: "hover:bg-white/10 "
        }
    };

    const colors = themeClasses[theme];

    // Determine dropdown positioning classes
    const getDropdownPositionClass = () => {
        switch (dropdownPosition) {
            case "right":
                return "right-0";
            case "center":
                return "left-1/2 -translate-x-1/2";
            case "left":
            default:
                return "left-0";
        }
    };

    return (
        <Menu as="div" className={`relative h-full sm:w-full ${buttonClassName}`}>
            {({ open, close }) => (
                <>
                    <Menu.Button
                        className={twClassNames(
                            "workspaceSwitcherControl group focus:outline-none focus:ring-0",
                            colors.workspaceSwitcherControl,
                            className
                        )}
                    >
                        {!hideChevron && (
                            <div
                                className={twClassNames(
                                    "far fa-arrow-up-arrow-down workspaceChevron flex-none text-xs",
                                    colors.chevron
                                )}
                            ></div>
                        )}
                        <div className="workspaceInfo min-w-0 flex-1 flex-col gap-1 font-sans">
                            <div className="flex flex-row items-center gap-[6px]">
                                <span
                                    className={twClassNames(
                                        "truncate text-sm font-medium",
                                        colors.workspaceName
                                    )}
                                >
                                    {currentWorkspace?.name}
                                </span>
                            </div>
                            <div className="flex flex-row items-center gap-1">
                                {planTitle && (
                                    <div className="flex flex-row items-center gap-1">
                                        <span
                                            className={twClassNames(
                                                "whitespace-nowrap text-left text-[10px] font-normal",
                                                colors.metadata
                                            )}
                                        >
                                            {planTitle}
                                        </span>
                                        <span className={`text-[10px] ${colors.metadata}`}>•</span>
                                    </div>
                                )}
                                <span
                                    className={twClassNames(
                                        "whitespace-nowrap text-left text-[10px] font-normal",
                                        colors.metadata
                                    )}
                                >
                                    {getWorkspaceMembersCountText(currentWorkspace?.members)}
                                </span>
                            </div>
                        </div>
                        <i
                            className={twClassNames(
                                "workspaceTrailingChevron far fa-arrow-down-arrow-up hidden flex-none pt-1 text-xs md:inline",
                                colors.trailingChevron
                            )}
                        ></i>
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Menu.Items
                            className={twClassNames(
                                "workspaceSwitcher menu-container absolute z-50 ml-2 max-h-[80vh] w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none",
                                getDropdownPositionClass(),
                                dropdownClassName
                            )}
                        >
                            <BaseWorkspaceSwitcher
                                user={user}
                                workspaces={workspaces}
                                workspaceUsages={workspaceUsages}
                                currentWorkspace={currentWorkspace}
                                onWorkspaceSelect={onWorkspaceSelect}
                                onItemClick={close}
                                searchThreshold={searchThreshold}
                                isMenu={true}
                                className="workspaceList"
                                createNewWorkspace={createNewWorkspace}
                            />
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );
}
